/**
 * P3 — verdict de plausibilité COMPOSÉ (règles BE + tuiles physiques FE).
 *
 * Angle mort corrigé (fix post-smoke L3) : le verdict était dérivé du seul
 * `summary` BE (règles), aveugle aux appréciations des tuiles calculées côté
 * FE → une tuile rouge pouvait cohabiter avec un verdict « Aucune incohérence ».
 * Ce module est la SOURCE UNIQUE du verdict, partagée entre le dashboard
 * (PlausibilitySection) et la pré-clôture (CloseModal).
 *
 * Les fourchettes de vraisemblance sont de l'AFFICHAGE FE (v1, calibrables sans
 * redeploy BE), pas un contrat backend.
 */

// Fourchettes v1 (validées PO, L3) — ordres de grandeur bâtiments/mobilité CH.
export const TILE_RANGES = {
  elec:    { green: [10, 150],    orangeLow: 3,   orangeHigh: 300 },
  heat:    { green: [30, 250],    orangeLow: 10,  orangeHigh: 400 },
  commute: { green: [500, 12000], orangeLow: 100, orangeHigh: 20000 },
};

export const TILE_KEYS = ['elec', 'heat', 'commute'];

// Clé du ratio dans chaque bloc physical_kpis.
const RATIO_KEY = { elec: 'kwh_m2', heat: 'kwh_m2', commute: 'km_fte' };

/**
 * Appréciation de vraisemblance physique d'une valeur.
 * @returns 'green' | 'orange' | 'red' | 'neutral' (ratio null → neutral).
 */
export function assessTile(kind, ratio) {
  if (ratio === null || ratio === undefined) return 'neutral';
  const r = TILE_RANGES[kind];
  if (!r) return 'neutral';
  // vert = fourchette usuelle (bornes INCLUSES) ; rouge = hors garde-fous
  // (bornes orange EXCLUES) ; orange = entre les deux.
  if (ratio >= r.green[0] && ratio <= r.green[1]) return 'green';
  if (ratio < r.orangeLow || ratio > r.orangeHigh) return 'red';
  return 'orange';
}

/** États des tuiles présentes (bloc non-null) : [{ key, state }]. */
export function tileStates(physicalKpis) {
  if (!physicalKpis) return [];
  return TILE_KEYS
    .map(key => ({ key, block: physicalKpis[key] }))
    .filter(({ block }) => block)
    .map(({ key, block }) => ({ key, state: assessTile(key, block[RATIO_KEY[key]]) }));
}

const LEVEL_ORDER = { ok: 0, warning: 1, critical: 2 };

/**
 * Compose le verdict à partir des règles (summary) ET des tuiles.
 * @returns {
 *   level: 'ok'|'warning'|'critical'|null,   // null si aucun résultat
 *   rulesLevel, tileLevel,                   // contribution de chaque source
 *   redTiles: [key], orangeTiles: [key],     // tuiles hors-vert (pour les libellés)
 * }
 * Mapping : tuile rouge → critical ; tuile orange → warning.
 */
export function composeVerdict(summary, physicalKpis) {
  if (!summary) return { level: null, rulesLevel: null, tileLevel: 'ok', redTiles: [], orangeTiles: [] };
  const rulesLevel = summary.critical > 0 ? 'critical' : summary.warning > 0 ? 'warning' : 'ok';

  const states = tileStates(physicalKpis);
  const redTiles = states.filter(s => s.state === 'red').map(s => s.key);
  const orangeTiles = states.filter(s => s.state === 'orange').map(s => s.key);
  const tileLevel = redTiles.length ? 'critical' : orangeTiles.length ? 'warning' : 'ok';

  const level = LEVEL_ORDER[rulesLevel] >= LEVEL_ORDER[tileLevel] ? rulesLevel : tileLevel;
  return { level, rulesLevel, tileLevel, redTiles, orangeTiles };
}
